import { scaleBand, scaleLinear, scalePoint } from '@visx/scale'
import { Group } from '@visx/group'
import { AxisBottom, AxisLeft, AxisRight } from '@visx/axis'
import { Bar, LinePath } from '@visx/shape'
import type { ChartResult, ChartSeriesPoint, ChartSpec } from '../../../types/chart.js'
import { CHART_AXIS_COLOR, CHART_GRID_COLOR, getSeriesStyle } from './chartTheme.js'
import './chart.css'

export interface ComboChartProps {
  spec: ChartSpec
  data: ChartResult
  width?: number
  height?: number
  className?: string
  testId?: string
}

export function ComboChart({
  spec,
  data,
  width = 500,
  height = 240,
  className = '',
  testId,
}: ComboChartProps) {
  const margin = { top: 20, right: 50, bottom: 40, left: 50 }
  const xMax = Math.max(0, width - margin.left - margin.right)
  const yMax = Math.max(0, height - margin.top - margin.bottom)

  const xKeys = Array.from(
    new Set(data.series.flatMap((s) => s.points.map((p) => String(p.x)))),
  )

  // Split series into bar (left) and line (right)
  // If series.axis is set, respect it; otherwise series[0] is bar, series[1+] are line
  const barSeriesList = data.series.filter((s, idx) =>
    s.axis === 'left' || (!s.axis && idx === 0),
  )
  const lineSeriesList = data.series.filter((s, idx) =>
    s.axis === 'right' || (!s.axis && idx > 0),
  )

  const leftYValues = barSeriesList.flatMap((s) => s.points.map((p) => p.y))
  const maxLeftY = Math.max(...leftYValues, 1)

  const rightYValues = lineSeriesList.flatMap((s) => s.points.map((p) => p.y))
  const maxRightY = Math.max(...rightYValues, 1)

  const xBandScale = scaleBand<string>({
    domain: xKeys,
    range: [0, xMax],
    padding: 0.35,
  })

  const xPointScale = scalePoint<string>({
    domain: xKeys,
    range: [0, xMax],
    padding: 0.5,
  })

  const yLeftScale = scaleLinear<number>({
    domain: [0, maxLeftY],
    range: [yMax, 0],
    nice: true,
  })

  const yRightScale = scaleLinear<number>({
    domain: [0, maxRightY],
    range: [yMax, 0],
    nice: true,
  })

  const barWidth = xBandScale.bandwidth() || 20

  return (
    <svg
      width={width}
      height={height}
      className={`chartSvg ${className}`}
      data-testid={testId}
      role="img"
      aria-label={spec.title || 'Combo Chart'}
    >
      <Group left={margin.left} top={margin.top}>
        {/* Bar series on left axis */}
        {barSeriesList.map((series, sIdx) => {
          const style = getSeriesStyle(sIdx, spec.style?.seriesColor?.[series.key])
          return (
            <Group key={series.key || `bar_${sIdx}`}>
              {series.points.map((point, pIdx) => {
                const xVal = String(point.x)
                const barX = xBandScale(xVal) || 0
                const barY = yLeftScale(point.y)
                const barHeight = Math.max(0, yMax - barY)
                return (
                  <Bar
                    key={pIdx}
                    x={barX}
                    y={barY}
                    width={barWidth}
                    height={barHeight}
                    fill={style.fill}
                    rx={2}
                  />
                )
              })}
            </Group>
          )
        })}

        {/* Line series on right axis */}
        {lineSeriesList.map((series, sIdx) => {
          const styleIndex = barSeriesList.length + sIdx
          const style = getSeriesStyle(styleIndex, spec.style?.seriesColor?.[series.key])
          return (
            <Group key={series.key || `line_${sIdx}`}>
              <LinePath<ChartSeriesPoint>
                data={series.points}
                x={(p) => xPointScale(String(p.x)) || 0}
                y={(p) => yRightScale(p.y)}
                stroke={style.color}
                strokeWidth={2.5}
                strokeDasharray={style.strokeDasharray}
              />
              {series.points.map((point, pIdx) => (
                <circle
                  key={pIdx}
                  cx={xPointScale(String(point.x)) || 0}
                  cy={yRightScale(point.y)}
                  r={3.5}
                  fill={style.color}
                />
              ))}
            </Group>
          )
        })}

        {/* X Axis */}
        <AxisBottom
          scale={xBandScale}
          top={yMax}
          stroke={CHART_AXIS_COLOR}
          tickStroke={CHART_GRID_COLOR}
          tickLabelProps={{
            fill: CHART_AXIS_COLOR,
            fontSize: 10,
            textAnchor: 'middle',
          }}
        />

        {/* Left Y Axis */}
        <AxisLeft
          scale={yLeftScale}
          stroke={CHART_AXIS_COLOR}
          tickStroke={CHART_GRID_COLOR}
          tickLabelProps={{
            fill: CHART_AXIS_COLOR,
            fontSize: 10,
            textAnchor: 'end',
            dx: -4,
            dy: 3,
          }}
        />

        {/* Right Y Axis */}
        {lineSeriesList.length > 0 && (
          <AxisRight
            scale={yRightScale}
            left={xMax}
            stroke={CHART_AXIS_COLOR}
            tickStroke={CHART_GRID_COLOR}
            tickLabelProps={{
              fill: CHART_AXIS_COLOR,
              fontSize: 10,
              textAnchor: 'start',
              dx: 4,
              dy: 3,
            }}
          />
        )}
      </Group>
    </svg>
  )
}
